import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { Timer, IndianRupee, TrendingUp, Percent, Download, ArrowRight } from "lucide-react";

export default function RetirementCalculatorPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/calculator/retirement`;
  const schemas = [
    webpageSchema(
      "Retirement Calculator | Corpus & Monthly SIP Planner | ExpertAdvise.in",
      "Calculate retirement corpus needed and monthly SIP required to retire comfortably. Plan your golden years with inflation-adjusted projections.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Calculators", url: `${baseUrl}/calculators` },
      { name: "Retirement Calculator", url: pageUrl },
    ]),
  ];
  const [currentAge, setCurrentAge] = useState(35);
  const [retirementAge, setRetirementAge] = useState(60);
  const [monthlyExpenses, setMonthlyExpenses] = useState(50000);
  const [inflation, setInflation] = useState(6);
  const [returnRate, setReturnRate] = useState(10);
  const [existingCorpus, setExistingCorpus] = useState(15);

  const results = useMemo(() => {
    const yearsToRetire = retirementAge - currentAge;
    const yearsInRetirement = 85 - retirementAge;
    const futureMonthlyExpense = monthlyExpenses * Math.pow(1 + inflation / 100, yearsToRetire);
    const annualRetirementExpense = futureMonthlyExpense * 12;
    const totalCorpusNeeded = annualRetirementExpense * ((1 - Math.pow(1 + (inflation - returnRate) / 100, yearsInRetirement)) / ((returnRate - inflation) / 100));
    const adjustedCorpus = Math.abs(totalCorpusNeeded);
    const monthlySIP = (adjustedCorpus - existingCorpus * 100000) * (returnRate / 100 / 12) / (Math.pow(1 + returnRate / 100 / 12, yearsToRetire * 12) - 1);
    return {
      yearsToRetire,
      futureMonthlyExpense: Math.round(futureMonthlyExpense),
      totalCorpusNeeded: Math.round(adjustedCorpus),
      monthlySIP: Math.max(0, Math.round(monthlySIP)),
      existingCorpusValue: existingCorpus * 100000,
    };
  }, [currentAge, retirementAge, monthlyExpenses, inflation, returnRate, existingCorpus]);

  const formatCurrency = useCallback((val: number) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(2)} Lakhs`;
    return `₹${val.toLocaleString()}`;
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Retirement Calculator | Corpus & Monthly SIP Planner | ExpertAdvise.in"
        description="Calculate retirement corpus needed and monthly SIP required to retire comfortably. Plan your golden years with inflation-adjusted projections."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />
      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-violet-50 text-violet-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Retirement Calculator</span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">Retirement Corpus Calculator</h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">Find out how much you need to save monthly to retire comfortably. Plan your golden years with confidence.</p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-5">Your Details</h3>
                <div className="space-y-5">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Timer className="w-4 h-4 text-primary-500" />Current Age</label>
                    <input type="range" min="20" max="60" value={currentAge} onChange={(e) => setCurrentAge(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>20</span><span className="font-semibold text-primary-700">{currentAge} Years</span><span>60</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Timer className="w-4 h-4 text-primary-500" />Retirement Age</label>
                    <input type="range" min="45" max="70" value={retirementAge} onChange={(e) => setRetirementAge(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>45</span><span className="font-semibold text-primary-700">{retirementAge} Years</span><span>70</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><IndianRupee className="w-4 h-4 text-primary-500" />Current Monthly Expenses</label>
                    <input type="range" min="20000" max="300000" step="5000" value={monthlyExpenses} onChange={(e) => setMonthlyExpenses(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹20K</span><span className="font-semibold text-primary-700">₹{monthlyExpenses.toLocaleString()}</span><span>₹3L</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Percent className="w-4 h-4 text-primary-500" />Expected Inflation (%)</label>
                    <input type="range" min="3" max="10" step="0.5" value={inflation} onChange={(e) => setInflation(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>3%</span><span className="font-semibold text-primary-700">{inflation}%</span><span>10%</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><TrendingUp className="w-4 h-4 text-primary-500" />Expected Return (%)</label>
                    <input type="range" min="6" max="15" step="0.5" value={returnRate} onChange={(e) => setReturnRate(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>6%</span><span className="font-semibold text-primary-700">{returnRate}%</span><span>15%</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><IndianRupee className="w-4 h-4 text-primary-500" />Existing Corpus (₹ Lakhs)</label>
                    <input type="range" min="0" max="200" step="5" value={existingCorpus} onChange={(e) => setExistingCorpus(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹0</span><span className="font-semibold text-primary-700">₹{existingCorpus}L</span><span>₹200L</span></div>
                  </div>
                </div>
              </div>
              <button onClick={() => alert("PDF report generation will be enabled after backend integration.")} className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 rounded-xl transition-colors"><Download className="w-4 h-4" />Download PDF Report</button>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-white/80 mb-1">Retirement Corpus Needed</div>
                    <div className="text-3xl md:text-4xl font-bold">{formatCurrency(results.totalCorpusNeeded)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-white/80 mb-1">Monthly SIP Required</div>
                    <div className="text-3xl md:text-4xl font-bold">{formatCurrency(results.monthlySIP)}/mo</div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/20 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><div className="text-xs text-white/60 mb-1">Years to Retire</div><div className="font-semibold">{results.yearsToRetire} Years</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Future Monthly Expense</div><div className="font-semibold">{formatCurrency(results.futureMonthlyExpense)}/mo</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Existing Corpus</div><div className="font-semibold">{formatCurrency(results.existingCorpusValue)}</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Retirement Duration</div><div className="font-semibold">{85 - retirementAge} Years</div></div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Key Insights</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-slate-50 rounded-xl"><div className="text-sm font-semibold text-slate-900">Start Early, Retire Rich</div><div className="text-xs text-slate-500 mt-0.5">Starting SIP at age 25 instead of 35 can reduce your monthly contribution by ~40% for the same corpus.</div></div>
                  <div className="p-4 bg-slate-50 rounded-xl"><div className="text-sm font-semibold text-slate-900">Beat Inflation</div><div className="text-xs text-slate-500 mt-0.5">At {inflation}% inflation, your ₹{monthlyExpenses.toLocaleString()}/month expenses will become ₹{results.futureMonthlyExpense.toLocaleString()}/month at retirement.</div></div>
                  <div className="p-4 bg-slate-50 rounded-xl"><div className="text-sm font-semibold text-slate-900">NPS + Mutual Funds Combo</div><div className="text-xs text-slate-500 mt-0.5">Use NPS for the disciplined 40% and equity mutual funds for the growth 60% of your retirement portfolio.</div></div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-5 md:p-6">
                <h3 className="text-lg font-bold text-emerald-900 mb-2">Recommended Retirement Strategy</h3>
                <p className="text-sm text-emerald-700 mb-4">Based on your profile, here is a suggested asset allocation:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl p-4 text-center"><div className="text-2xl font-bold text-emerald-600">40%</div><div className="text-sm font-medium text-slate-700">Equity Mutual Funds</div><div className="text-xs text-slate-500">Growth engine</div></div>
                  <div className="bg-white rounded-xl p-4 text-center"><div className="text-2xl font-bold text-emerald-600">30%</div><div className="text-sm font-medium text-slate-700">NPS Tier I</div><div className="text-xs text-slate-500">Tax benefit + pension</div></div>
                  <div className="bg-white rounded-xl p-4 text-center"><div className="text-2xl font-bold text-emerald-600">30%</div><div className="text-sm font-medium text-slate-700">PPF + Debt Funds</div><div className="text-xs text-slate-500">Stability + safety</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">Plan Your Retirement with an Expert</h2>
          <p className="text-slate-600 mb-6">These are estimates. Our advisors create personalized retirement strategies based on your unique situation.</p>
          <Link to="/book-appointment?source=Retirement%20Calculator%20Page" className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap">Book Free Consultation</Link>
        </div>
      </section>
      <Footer /><FloatingCTA />
    </div>
  );
}